import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import './WhatsAppConnect.css';

const WhatsAppConnect = () => {
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [newInstanceName, setNewInstanceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [limitInfo, setLimitInfo] = useState({ limit: 0, used: 0 });

  useEffect(() => {
    let intervalId;

    const fetchInstances = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/whatsapp/instances`);
        const payload = response.data || {};
        const list = payload.instances || payload;
        setInstances(list || []);
        setLimitInfo({ limit: payload.limit || 0, used: payload.used || (list || []).length });
        if (!selectedInstance && list?.length) {
          setSelectedInstance(list[0].name);
        }
      } catch (err) {}
    };

    const fetchQr = async () => {
      if (!selectedInstance) return;
      try {
        const response = await axios.get(`${apiBaseUrl}/whatsapp/qr?instance=${encodeURIComponent(selectedInstance)}`);
        setQr(response.data.qr);
        setStatus(response.data.status || 'unknown');
        setError(null);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setQr(null);
          setStatus(err.response.data.status || 'waiting');
          setError(null);
        } else {
          setError('Falha ao buscar QR Code.');
        }
      }
    };

    fetchInstances();
    fetchQr();
    intervalId = setInterval(fetchQr, 5000);

    return () => clearInterval(intervalId);
  }, [selectedInstance]);

  const handleCreateInstance = async () => {
    if (!newInstanceName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await axios.post(`${apiBaseUrl}/whatsapp/instances`, { name: newInstanceName.trim() });
      const response = await axios.get(`${apiBaseUrl}/whatsapp/instances`);
      const payload = response.data || {};
      const list = payload.instances || payload;
      setInstances(list || []);
      setLimitInfo({ limit: payload.limit || 0, used: payload.used || (list || []).length });
      setSelectedInstance(newInstanceName.trim());
      setNewInstanceName('');
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Falha ao criar instância.';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteInstance = async (name) => {
    if (!name) return;
    try {
      await axios.delete(`${apiBaseUrl}/whatsapp/instances/${encodeURIComponent(name)}`);
      const response = await axios.get(`${apiBaseUrl}/whatsapp/instances`);
      const payload = response.data || {};
      const list = payload.instances || payload;
      setInstances(list || []);
      setLimitInfo({ limit: payload.limit || 0, used: payload.used || (list || []).length });
      if (selectedInstance === name) {
        setSelectedInstance(list?.[0]?.name || '');
        setQr(null);
        setStatus('unknown');
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Falha ao excluir instância.';
      setError(message);
    }
  };

  return (
    <div className="whatsapp-connect">
      <div className="whatsapp-header">
        <div>
          <h2>Instâncias do WhatsApp</h2>
          <p>Crie e monitore múltiplas instâncias de atendimento.</p>
        </div>
        <div className="whatsapp-create">
          <input
            type="text"
            placeholder="Nome da instância (ex: Instancia 02)"
            value={newInstanceName}
            onChange={(e) => setNewInstanceName(e.target.value)}
          />
          <button onClick={handleCreateInstance} disabled={creating || (limitInfo.limit && limitInfo.used >= limitInfo.limit)}>
            {creating ? 'Criando...' : 'Adicionar'}
          </button>
        </div>
      </div>
      {limitInfo.limit ? (
        <p className="instance-limit">Instâncias: {limitInfo.used}/{limitInfo.limit}</p>
      ) : null}

      {error && <p className="error-message">{error}</p>}

      <div className="whatsapp-grid">
        <div className="whatsapp-list">
          {instances.length === 0 && <p>Nenhuma instância cadastrada.</p>}
          {instances.map((instance) => (
            <button
              key={instance.name}
              className={`whatsapp-instance ${selectedInstance === instance.name ? 'active' : ''}`}
              onClick={() => setSelectedInstance(instance.name)}
            >
              <div>
                <strong>{instance.name}</strong>
                <small>Session: {instance.session}</small>
              </div>
              <div className="whatsapp-instance-actions">
                <span className={`status-pill status-${instance.status || 'unknown'}`}>{instance.status || 'unknown'}</span>
                <button className="whatsapp-delete" onClick={(e) => { e.stopPropagation(); handleDeleteInstance(instance.name); }}>
                  Excluir
                </button>
              </div>
            </button>
          ))}
        </div>

        <div className="whatsapp-panel">
          <div className="whatsapp-panel-header">
            <h3>{selectedInstance || 'Selecione uma instância'}</h3>
            <span className={`status-pill status-${status}`}>{status}</span>
          </div>
          {qr ? (
            <div className="qr-box">
              <img src={qr} alt="QR Code WhatsApp" />
              <p>Abra o WhatsApp no celular e escaneie o QR Code.</p>
            </div>
          ) : (
            <div className="qr-placeholder">
              <p>QR Code ainda não disponível. Aguarde a sessão iniciar.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnect;
